import React, { useEffect, useMemo, useState } from 'react';
import client from '../../../api/client';
import ReportParamsForm, { validateReportParams } from './ReportParamsForm';
import SchedulePicker from './SchedulePicker';

/**
 * Modal for creating / editing a ReportingEvent. Holds three regions:
 *   1. Identity — name, recipient, subject, body intro
 *   2. Schedule — frequency + time + timezone, with a live next-run preview
 *   3. Items — ordered list of report invocations (each editable inline)
 *
 * All state lives here; on Save it POSTs/PUTs to /api/reporting/events and
 * calls back into the parent so the table can refresh.
 */
function EventEditor({
  open,
  initialEvent,
  reports,
  customers,
  savedFilters,
  csReps,
  users,
  onClose,
  onSaved,
}) {
  const [name, setName] = useState('');
  const [recipientUserId, setRecipientUserId] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyIntro, setBodyIntro] = useState('');
  const [scheduleType, setScheduleType] = useState('Weekly');
  const [scheduleConfig, setScheduleConfig] = useState({ dayOfWeek: 1, hour: 7, minute: 0 });
  const [timeZone, setTimeZone] = useState('America/Chicago');
  const [enabled, setEnabled] = useState(true);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [nextRunPreview, setNextRunPreview] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (initialEvent) {
      setName(initialEvent.name || '');
      setRecipientUserId(initialEvent.recipientUserId || '');
      setSubject(initialEvent.subject || '');
      setBodyIntro(initialEvent.bodyIntro || '');
      setScheduleType(initialEvent.scheduleType || 'Weekly');
      setScheduleConfig(initialEvent.scheduleConfig || { dayOfWeek: 1, hour: 7, minute: 0 });
      setTimeZone(initialEvent.timeZone || 'America/Chicago');
      setEnabled(initialEvent.enabled !== false);
      setItems((initialEvent.items || []).map(i => ({
        ...i,
        parameters: i.parameters || {
          scope: { type: 'all' },
          dateRange: { mode: 'last_n_days', n: 30 },
          removeTestData: true,
        },
      })));
      setNextRunPreview(initialEvent.nextRunAt || null);
    } else {
      setName('');
      setRecipientUserId('');
      setSubject('');
      setBodyIntro('');
      setScheduleType('Weekly');
      setScheduleConfig({ dayOfWeek: 1, hour: 7, minute: 0 });
      setTimeZone('America/Chicago');
      setEnabled(true);
      setItems([]);
      setNextRunPreview(null);
    }
    setError('');
  }, [open, initialEvent]);

  // Local "next run" estimate. The server is the source of truth on save,
  // but a local approximation keeps the UI responsive while the user fiddles.
  useEffect(() => {
    setNextRunPreview(estimateNextRun(scheduleType, scheduleConfig, timeZone));
  }, [scheduleType, scheduleConfig, timeZone]);

  const reportsById = useMemo(() => {
    const map = new Map();
    for (const r of reports || []) map.set(r.id, r);
    return map;
  }, [reports]);

  if (!open) return null;

  // Per-item validity comes from the same shared validator the RunNowDialog
  // uses so the two surfaces never drift apart on what counts as "complete".
  const itemErrors = items.map(i =>
    validateReportParams(reportsById.get(i.reportFunctionId)?.parameters, i.parameters)
  );
  const allItemsValid = itemErrors.every(e => e === null);

  const isValid =
    name.trim().length > 0 &&
    recipientUserId.trim().length > 0 &&
    items.length > 0 &&
    items.every(i => !!i.reportFunctionId) &&
    allItemsValid;

  const addItem = (reportId) => {
    const report = reportsById.get(reportId);
    if (!report) return;
    setItems(prev => [...prev, {
      reportFunctionId: reportId,
      parameters: {
        scope: { type: report.parameters?.defaultScopeKind || 'all' },
        dateRange: report.parameters?.defaultDateRange || { mode: 'last_n_days', n: 30 },
        repSelection: report.parameters?.supportsRepSelection ? { mode: 'all' } : undefined,
        removeTestData: true,
        removeSelfReferrals: report.parameters?.supportsRemoveSelfReferrals ? false : undefined,
        delivery: 'attachment',
        consultEventNames: report.parameters?.supportsScheduleEventAliases ? [] : undefined,
        procedureEventNames: report.parameters?.supportsScheduleEventAliases ? [] : undefined,
      },
      displayOrder: prev.length,
    }]);
  };

  const updateItem = (idx, patch) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx).map((it, i) => ({ ...it, displayOrder: i })));
  };

  const moveItem = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    setItems(prev => {
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((it, i) => ({ ...it, displayOrder: i }));
    });
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = {
        name: name.trim(),
        recipientUserId: recipientUserId.trim(),
        subject: subject.trim(),
        bodyIntro,
        scheduleType,
        scheduleConfig,
        timeZone,
        enabled,
        items: items.map((i, idx) => ({
          reportFunctionId: i.reportFunctionId,
          parameters: i.parameters,
          displayOrder: idx,
        })),
      };
      const res = initialEvent?.id
        ? await client.put(`/api/reporting/events/${initialEvent.id}`, body)
        : await client.post('/api/reporting/events', body);
      onSaved?.(res.data?.event);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="agentic-modal-backdrop" onClick={onClose}>
      <div className="agentic-modal agentic-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="agentic-modal-header">
          <h3>{initialEvent?.id ? `Edit event — ${initialEvent.name}` : 'New scheduled event'}</h3>
          <button className="agentic-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="agentic-modal-body">
          <div className="agentic-ed-grid">
            <div className="agentic-ed-section">
              <h4>Identity</h4>
              <div className="agentic-ed-field">
                <label>Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Weekly Memorial Hermann brief"
                />
              </div>
              <div className="agentic-ed-field">
                <label>Recipient (CS rep)</label>
                <select value={recipientUserId} onChange={(e) => setRecipientUserId(e.target.value)}>
                  <option value="">— Pick a user —</option>
                  {(users || []).map(u => (
                    <option key={u.id} value={u.id}>
                      {(u.firstName || u.lastName) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : u.email}
                      {u.email ? ` <${u.email}>` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="agentic-ed-field">
                <label>Email subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. Your weekly collaboration brief"
                />
              </div>
              <div className="agentic-ed-field">
                <label>Body intro</label>
                <textarea
                  rows={3}
                  value={bodyIntro}
                  onChange={(e) => setBodyIntro(e.target.value)}
                  placeholder="Optional intro text shown above the report content."
                />
              </div>
              <label className="agentic-ed-toggle">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                Enabled
              </label>
            </div>

            <div className="agentic-ed-section">
              <h4>Schedule</h4>
              <SchedulePicker
                scheduleType={scheduleType}
                scheduleConfig={scheduleConfig}
                timeZone={timeZone}
                nextRunAt={nextRunPreview}
                onChange={(next) => {
                  setScheduleType(next.scheduleType);
                  setScheduleConfig(next.scheduleConfig);
                  setTimeZone(next.timeZone);
                }}
              />
            </div>
          </div>

          <div className="agentic-ed-section agentic-ed-items">
            <div className="agentic-ed-items-head">
              <h4>Reports in this event</h4>
              <select
                value=""
                onChange={(e) => { if (e.target.value) addItem(e.target.value); e.target.value = ''; }}
              >
                <option value="">+ Add report…</option>
                {(reports || [])
                  .filter(r => !r.parameters?.excludesFromScheduledEvents)
                  .map(r => (
                    <option key={r.id} value={r.id}>{r.displayName} ({(r.outputFormat || '').toUpperCase()})</option>
                  ))}
              </select>
            </div>

            {items.length === 0 && (
              <div className="agentic-hint">No reports added yet. Pick one above to get started.</div>
            )}

            <ol className="agentic-ed-item-list">
              {items.map((item, idx) => {
                const report = reportsById.get(item.reportFunctionId);
                return (
                  <li key={idx} className="agentic-ed-item">
                    <div className="agentic-ed-item-head">
                      <div className="agentic-ed-item-title">
                        <strong>{report?.displayName || item.reportFunctionId}</strong>
                        <span className="agentic-format-pill">{(report?.outputFormat || '').toUpperCase()}</span>
                      </div>
                      <div className="agentic-ed-item-actions">
                        <button type="button" onClick={() => moveItem(idx, -1)} disabled={idx === 0}>↑</button>
                        <button type="button" onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1}>↓</button>
                        <button type="button" className="danger" onClick={() => removeItem(idx)}>Remove</button>
                      </div>
                    </div>

                    <ReportParamsForm
                      schema={report?.parameters}
                      value={item.parameters}
                      onChange={(next) => updateItem(idx, { parameters: { ...item.parameters, ...next } })}
                      customers={customers}
                      savedFilters={savedFilters}
                      csReps={csReps}
                    />

                    {itemErrors[idx] && (
                      <div className="rpf-warn">{itemErrors[idx]}</div>
                    )}

                    {report?.outputFormat === 'text' && (
                      <div className="agentic-ed-field">
                        <label>Delivery</label>
                        <select
                          value={item.parameters?.delivery || 'attachment'}
                          onChange={(e) => updateItem(idx, {
                            parameters: { ...item.parameters, delivery: e.target.value },
                          })}
                        >
                          <option value="attachment">Attachment (.txt file)</option>
                          <option value="body">Email body (when scheduler is enabled)</option>
                        </select>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          {error && <div className="agentic-error">{error}</div>}
        </div>

        <div className="agentic-modal-footer">
          <button type="button" className="agentic-btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="agentic-btn-primary"
            onClick={save}
            disabled={saving || !isValid}
          >
            {saving ? 'Saving…' : (initialEvent?.id ? 'Save changes' : 'Create event')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Quick local estimate of the next fire so the preview updates before save. */
function estimateNextRun(type, cfg, _tz) {
  if (!type || !cfg) return null;
  const now = new Date();
  const out = new Date(now);
  switch (type) {
    case 'Daily': {
      out.setHours(cfg.hour ?? 7, cfg.minute ?? 0, 0, 0);
      if (out <= now) out.setDate(out.getDate() + 1);
      return out.toISOString();
    }
    case 'Weekly': {
      out.setHours(cfg.hour ?? 7, cfg.minute ?? 0, 0, 0);
      const target = cfg.dayOfWeek ?? 1;
      let diff = (target - out.getDay() + 7) % 7;
      if (diff === 0 && out <= now) diff = 7;
      out.setDate(out.getDate() + diff);
      return out.toISOString();
    }
    case 'Monthly': {
      const dom = Math.min(cfg.dayOfMonth ?? 1, 28);
      const candidate = new Date(now.getFullYear(), now.getMonth(), dom, cfg.hour ?? 7, cfg.minute ?? 0, 0);
      if (candidate <= now) candidate.setMonth(candidate.getMonth() + 1);
      return candidate.toISOString();
    }
    case 'Quarterly': {
      // Approximation: just show the configured time on the matching day
      // of the current quarter, advancing if it's already passed.
      const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      const dayInQ = (cfg.weekOfQuarter ?? 1) * 7 + (cfg.dayOfWeek ?? 1) - 7;
      const candidate = new Date(qStart);
      candidate.setDate(qStart.getDate() + dayInQ);
      candidate.setHours(cfg.hour ?? 7, cfg.minute ?? 0, 0, 0);
      if (candidate <= now) {
        candidate.setMonth(candidate.getMonth() + 3);
      }
      return candidate.toISOString();
    }
    case 'OneTime': {
      if (!cfg.runAt) return null;
      const dt = new Date(cfg.runAt);
      return isNaN(dt.getTime()) ? null : dt.toISOString();
    }
    default:
      return null;
  }
}

export default EventEditor;
